import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { AdendaField } from "@/components/features/adenda/AdendaField";

export interface InvoiceAdendaSectionProps {
  /** The stored standing adenda; null when none is set. */
  savedAdenda: string | null;
  saving: boolean;
  disabled?: boolean;
  /** Empty string saves as "no standing adenda" (stored NULL by the backend). */
  onSave: (adendaText: string) => Promise<void>;
}

/**
 * The tenant's STANDING adenda — the CFE Zona J text that goes on every invoice
 * unless a marca overrides it or the invoice suppresses it.
 *
 * Rendered UNFRAMED: its one caller, CompanyAdendaCard, supplies the Card. It
 * first shipped inside the invoice-identity dialog, behind the "Editar" button
 * of a card titled "Logo de la empresa" — where nobody found it. An adenda is
 * fiscal text on a legal document, not presentation, so it earns a card of its
 * own rather than a slot in the branding editor.
 */
export function InvoiceAdendaSection({
  savedAdenda,
  saving,
  disabled,
  onSave,
}: InvoiceAdendaSectionProps): JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(savedAdenda ?? "");

  // Re-seed when the stored value changes under us (a save elsewhere, a tenant
  // switch). Without this the field keeps showing the previous tenant's text.
  useEffect(() => {
    setDraft(savedAdenda ?? "");
  }, [savedAdenda]);

  const dirty = draft.trim() !== (savedAdenda ?? "").trim();

  return (
    <div className="space-y-3">
      <AdendaField
        id="company-adenda"
        value={draft}
        onChange={setDraft}
        disabled={disabled || saving}
        label={t("adenda.company.label", "Texto de la empresa")}
        placeholder={t(
          "adenda.company.placeholder",
          "Texto que querés que aparezca en todas tus facturas."
        )}
        // The retroactivity point is the one a user will otherwise assume
        // wrongly: the adenda is frozen onto each invoice when it is issued, so
        // fixing a typo here does not fix the invoices already sent.
        helper={t(
          "adenda.company.helper",
          "Se imprime al pie de cada factura, en un recuadro rotulado ADENDA. Solo afecta a las facturas que emitas a partir de ahora: las ya emitidas conservan la adenda que llevaban."
        )}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => void onSave(draft.trim())}
          disabled={!dirty || saving || disabled}
          className="transition-colors duration-150"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("common.saving", "Guardando...")}
            </>
          ) : (
            t("common.save", "Guardar")
          )}
        </Button>
      </div>
    </div>
  );
}
